import React from 'react'
import PropTypes from 'prop-types'
import { CirclePicker } from 'react-color'
import { connect } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import ImmutablePureComponent from 'react-immutable-pure-component'
import { changeSetting, saveSettings } from '../../actions/settings'
import {
  DEFAULT_THEME,
  DEFAULT_FONT_SIZE,
  FONT_SIZES,
  AVAILABLE_ACCENT_COLORS,
  COLOR_BRAND,
} from '../../constants'
import ModalLayout from './modal_layout'
import Button from '../button'
import Text from '../text'
import Slider from '../slider'
import SettingSwitch from '../setting_switch'
import ResponsiveClassesComponent from '../../features/ui/util/responsive_classes_component'

/**
 * The DisplayOptions component was originally used in only one place in the
 * modal popup. Later it was exported to be used separately in other pages like
 * Introduction.
 */
class DisplayOptions extends ImmutablePureComponent {

  handleOnFontSizeChange = (value) => {
    const fontSizeNames = Object.keys(FONT_SIZES)
    const index = fontSizeNames[value]

    this.props.onChange('fontSize', index)
  }

  handleOnThemeSelected = (e) => {
    this.props.onChange('theme', e.target.value)
  }

  handleOnRadiusKeyDisabled = (key, checked) => {
    this.props.onChange(key, checked)
  }

  handleOnChangeColor = ({ hex }) => {
    const cleanAccentColor = AVAILABLE_ACCENT_COLORS.indexOf(hex) > -1 ? hex : COLOR_BRAND
    this.props.onChange('accentColor', cleanAccentColor)
  }

  render() {
    const {
      fontSize,
      displayOptionsSettings,
      theme,
      onClose,
      showOptions,
      showDone,
      accentColor,
    } = this.props

    const fontSizeNames = Object.keys(FONT_SIZES)
    const currentFontSizeIndex = fontSizeNames.indexOf(fontSize)

    // Options and done are optional and hidden on Introduction to simplify
    const optionsOuterClasses = showOptions ? [_s.d, _s.mb15].join(' ') : _s.displayNone

    const cleanAccentColor = AVAILABLE_ACCENT_COLORS.indexOf(accentColor) > -1 ? accentColor : COLOR_BRAND

    return (
      <>

        <div className={[_s.d, _s.mb15].join(' ')}>
          <Text color='secondary' size='medium'>
            Display settings affect your Gab account on this device.
          </Text>
        </div>

        <div className={[_s.d, _s.mb15].join(' ')}>
          <Text weight='bold' size='small' color='secondary'>
            Font Size
          </Text>
          <div className={[_s.d, _s.radiusSmall, _s.mt10, _s.py15, _s.px15, _s.bgTertiary].join(' ')}>
            <div className={[_s.d, _s.flexRow, _s.aiCenter].join(' ')}>
              <span className={[_s.d, _s.text, _s.cPrimary].join(' ')} style={{fontSize: '12px'}}>
                Aa
              </span>
              <Slider
                min={0}
                value={currentFontSizeIndex}
                max={fontSizeNames.length - 1}
                onInput={this.handleOnFontSizeChange}
                onChange={this.handleOnFontSizeChange}
                className={[_s.flexGrow1, _s.outlineNone, _s.ml15, _s.mr15].join(' ')}
              />
              <span className={[_s.d, _s.text, _s.cPrimary].join(' ')} style={{fontSize: '18px'}}>
                Aa
              </span>
            </div>
          </div>
        </div>

        <div className={optionsOuterClasses}>
          <Text weight='bold' size='small' color='secondary'>
            Options
          </Text>
          <div className={[_s.d, _s.radiusSmall, _s.mt10, _s.py5, _s.px15, _s.bgTertiary].join(' ')}>
            <SettingSwitch
              prefix='displayOptions'
              settings={displayOptionsSettings}
              settingPath={'radiusSmallDisabled'}
              onChange={this.handleOnRadiusKeyDisabled}
              label={'Small Radius Disabled'}
            />

            <SettingSwitch
              prefix='displayOptions'
              settings={displayOptionsSettings}
              settingPath={'radiusCircleDisabled'}
              onChange={this.handleOnRadiusKeyDisabled}
              label={'Circles Disabled'}
            />

            <SettingSwitch
              prefix='displayOptions'
              settings={displayOptionsSettings}
              settingPath={'logoDisabled'}
              onChange={this.handleOnRadiusKeyDisabled}
              label={'Stealth Gab'}
            />

            <SettingSwitch
              prefix='displayOptions'
              settings={displayOptionsSettings}
              settingPath={'fabDisabled'}
              onChange={this.handleOnRadiusKeyDisabled}
              label={'Floating Compose Button Disabled'}
            />

            <SettingSwitch
              prefix='displayOptions'
              settings={displayOptionsSettings}
              settingPath={'compactChatMessages'}
              onChange={this.handleOnRadiusKeyDisabled}
              label={'Compact Chat Messages'}
            />
          </div>
        </div>

        <div className={[_s.d, _s.mb10].join(' ')}>
          <Text weight='bold' size='small' color='secondary'>
            Accent Color
          </Text>
          <div className={[_s.d, _s.radiusSmall, _s.mt10, _s.py15, _s.bgTertiary].join(' ')}>
            <div className={[_s.d, _s.flexRow, _s.flexWrap, _s.px15].join(' ')}>
              <CirclePicker
                width='100%'
                circleSize={36}
                circleSpacing='2px'
                onChange={this.handleOnChangeColor}
                color={cleanAccentColor}
                className={[_s.d, _s.flexRow, _s.flexWrap, _s.jcSpaceBetween].join(' ')}
                colors={AVAILABLE_ACCENT_COLORS}
              />
            </div>
          </div>
        </div>

        <div className={[_s.d, _s.mb10].join(' ')}>
          <Text weight='bold' size='small' color='secondary'>
            Theme
          </Text>
          <div className={[_s.d, _s.radiusSmall, _s.mt10, _s.py15, _s.bgTertiary].join(' ')}>
            <div className={[_s.d, _s.flexRow, _s.flexWrap, _s.px10].join(' ')}>

              <ThemeBlock
                title='White'
                value='white'
                checked={theme === 'white'}
                onChange={this.handleOnThemeSelected}
                style={{
                  borderColor: '#ececed',
                  backgroundColor: '#fff',
                  color: '#2d3436',
                }}
              />

              <ThemeBlock
                title='Light'
                value='light'
                checked={theme === 'light'}
                onChange={this.handleOnThemeSelected}
                style={{
                  borderColor: cleanAccentColor,
                  backgroundColor: '#fff',
                  color: '#2d3436',
                }}
              />

              <ThemeBlock
                title='Muted'
                value='muted'
                checked={theme === 'muted'}
                onChange={this.handleOnThemeSelected}
                style={{
                  borderColor: '#424141',
                  backgroundColor: '#222',
                  color: '#fff',
                }}
              />

              <ThemeBlock
                title='Night'
                value='night'
                checked={theme === 'night'}
                onChange={this.handleOnThemeSelected}
                style={{
                  borderColor: '#222637',
                  backgroundColor: '#29384c',
                  color: '#d3d8f0',
                }}
              />

              <ThemeBlock
                title='Black'
                value='black'
                checked={theme === 'black'}
                onChange={this.handleOnThemeSelected}
                style={{
                  borderColor: '#212020',
                  backgroundColor: '#13171b',
                  color: '#cccbcb',
                }}
              />

            </div>
          </div>
        </div>
        {
          showDone &&
          <Button onClick={onClose} isBlock radiusSmall>
            <Text size='large' color='inherit' align='center' className={_s.px10}>
              Save changes
            </Text>
          </Button>
        }

      </>
    )
  }

}

const mapStateToProps = (state) => ({
  displayOptionsSettings: state.getIn(['settings', 'displayOptions']),
  fontSize: state.getIn(['settings', 'displayOptions', 'fontSize'], DEFAULT_FONT_SIZE),
  accentColor: state.getIn(['settings', 'displayOptions', 'accentColor'], COLOR_BRAND),
  theme: state.getIn(['settings', 'displayOptions', 'theme'], DEFAULT_THEME),
})

const mapDispatchToProps = (dispatch) => ({
  onChange(key, value) {
    dispatch(changeSetting(['displayOptions', key], value))
    dispatch(saveSettings())
  },
})

DisplayOptions.defaultProps = {
  showOptions: true,
  showDone: true,
}

DisplayOptions.propTypes = {
  fontSize: PropTypes.string,
  accentColor: PropTypes.string,
  displayOptionsSettings: ImmutablePropTypes.map,
  theme: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  showOptions: PropTypes.bool,
  showDone: PropTypes.bool
}

DisplayOptions = connect(mapStateToProps, mapDispatchToProps)(DisplayOptions)
export { DisplayOptions }

class ThemeBlock extends React.PureComponent {

  render() {
    const {
      checked,
      onChange,
      title,
      value,
      style,
    } = this.props

    const id = `theme-${value}`

    return (
      <ResponsiveClassesComponent
        classNames={[_s.d, _s.px5, _s.flexGrow1].join(' ')}
        classNamesXS={[_s.d, _s.px10, _s.flexGrow1, _s.w48PC, _s.mb10].join(' ')}
      >
      <label className={[_s.d, _s.w100PC].join(' ')} htmlFor={id}>
        <div
          className={[_s.d, _s.borderBottom6PX, _s.aiCenter, _s.flexRow, _s.py10, _s.px10, _s.radiusSmall].join(' ')}
          style={style}
        >
          <input
            type='radio'
            name='theme'
            value={value}
            id={id}
            checked={checked}
            onChange={onChange}
          />
          <Text
            align='center'
            size='medium'
            weight='bold'
            color='inherit'
            className={[_s.py10, _s.flexGrow1].join(' ')}
          >
            {title}
          </Text>
        </div>
      </label>
      </ResponsiveClassesComponent>
    )
  }

}

ThemeBlock.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  title: PropTypes.string,
  value: PropTypes.string,
  style: PropTypes.object,
}

class DisplayOptionsModal extends React.Component {
  render() {
    const { onClose } = this.props
    return (
      <ModalLayout
        onClose={onClose}
        width={625}
        title='Customize your view'
      >
        <DisplayOptions onClose={onClose}/>
      </ModalLayout>
    )
  }
}

DisplayOptionsModal.propTypes = {
  onClose: PropTypes.func.isRequired
}

export default DisplayOptionsModal